/**
 * Configuration schema using Zod for runtime validation
 */

import { z } from "zod";
import { DEFAULT_FEATURES, FEATURE_IDS, normalizeFeatures } from "./features.js";

/**
 * Environment configuration schema
 */
export const EnvironmentSchema = z.object({
  /** Environment ID (from Power Platform API) */
  id: z.string().min(1),
  /** Azure region (e.g., "unitedstates", "europe") */
  region: z.string().min(1),
  /** Human-readable name */
  displayName: z.string().optional(),
  /**
   * Optional override for the Dataverse hostname (e.g. "contoso.crm.dynamics.com").
   * When omitted, the MCP resolves it at startup via the BAP admin API and falls
   * back to a region-based guess if that call fails.
   */
  dataverseUrl: z.string().optional(),
});

export type EnvironmentConfig = z.infer<typeof EnvironmentSchema>;

/**
 * Authentication configuration schema
 */
export const AuthConfigSchema = z.object({
  /** Microsoft Entra application (client) ID */
  clientId: z.guid(),
  /**
   * Tenant ID - null for multi-tenant
   * Can be GUID, "common", "organizations", or "consumers"
   */
  tenantId: z.string().nullable().default(null),
});

export type AuthConfig = z.infer<typeof AuthConfigSchema>;

/**
 * Enabled feature/tool surfaces. Missing config defaults to the historical
 * behavior: all tool surfaces are enabled.
 */
export const FeatureIdSchema = z.enum(FEATURE_IDS);

export const FeaturesConfigSchema = z.object({
  enabled: z
    .array(FeatureIdSchema)
    .default([...DEFAULT_FEATURES])
    .transform((features) => normalizeFeatures(features)),
});

export type FeaturesConfig = z.infer<typeof FeaturesConfigSchema>;

/**
 * Cache configuration schema
 */
export const CacheConfigSchema = z.object({
  /** Custom path for schema cache SQLite DB */
  schemaPath: z.string().nullable().default(null),
  /** Hours between connector schema refreshes */
  refreshIntervalHours: z.number().min(1).max(168).default(24),
});

export type CacheConfig = z.infer<typeof CacheConfigSchema>;

/**
 * Logging configuration schema
 */
export const LoggingConfigSchema = z.object({
  /** Log level */
  level: z.enum(["fatal", "error", "warn", "info", "debug", "trace"]).default("info"),
});

export type LoggingConfig = z.infer<typeof LoggingConfigSchema>;

/**
 * Environments configuration - maps aliases to environment configs
 */
export const EnvironmentsSchema = z
  .object({
    /** Default environment name (must match a key in the environments object) */
    default: z.string().min(1),
  })
  .catchall(EnvironmentSchema);

export type EnvironmentsConfig = z.infer<typeof EnvironmentsSchema>;

/**
 * Complete configuration schema
 */
export const ConfigSchema = z.object({
  auth: AuthConfigSchema,
  features: FeaturesConfigSchema.prefault({ enabled: [...DEFAULT_FEATURES] }),
  environments: EnvironmentsSchema,
  cache: CacheConfigSchema.prefault({}),
  logging: LoggingConfigSchema.prefault({}),
});

export type Config = z.infer<typeof ConfigSchema>;

/**
 * Validate a config object
 * @throws ZodError if validation fails
 */
export function validateConfig(config: unknown): Config {
  const result = ConfigSchema.parse(config);

  // Additional validation: default environment must exist
  const envNames = Object.keys(result.environments).filter(k => k !== "default");
  if (!envNames.includes(result.environments.default)) {
    throw new Error(
      `Default environment "${result.environments.default}" not found. ` +
      `Available: ${envNames.join(", ")}`
    );
  }

  return result;
}

/**
 * Get default config structure (for initialization)
 */
export function getDefaultConfig(): Record<string, unknown> {
  return {
    auth: {
      clientId: "00000000-0000-0000-0000-000000000000",
      tenantId: null,
    },
    features: {
      enabled: [...DEFAULT_FEATURES],
    },
    environments: {
      default: "dev",
      dev: {
        id: "00000000-0000-0000-0000-000000000000",
        region: "unitedstates",
        displayName: "Development",
      },
    },
    cache: {
      schemaPath: null,
      refreshIntervalHours: 24,
    },
    logging: {
      level: "info",
    },
  };
}
